// version2026: headless month-view calendar logic — the shared, reusable CORE.
// PURE logic only: month state, day-grid generation, today/selected state, booking-dot data
// mapping. NO fetch, NO DOM, NO styling. A future redesign changes only the style layer
// (MonthCalendarV2026.vue), never this file. Vue 2.7 native Composition API (no plugin).
//
// Extracted 1:1 from the inline calendar in CustomerPanelV2026.vue (calendarCells /
// ordersByDayMap / prevMonth / nextMonth), plus additive isToday/isSelected state.
import { ref, computed, isRef, watch } from 'vue'
import moment from 'moment'

// Thai month names — exported so the STYLE layer can format the title (Buddhist vs Gregorian,
// Thai vs other) without the core deciding presentation.
export const THAI_MONTHS = [
  'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
  'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
]

// Language-agnostic default weekday base (Sun-first). The wrapper/prop can override the labels;
// the core only needs 7 entries to rotate by weekStartsOn.
export const DEFAULT_WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

// Accept a ref, a getter fn, or a plain value — keeps the core usable from Options or Composition.
function read (v) {
  if (isRef(v)) return v.value
  if (typeof v === 'function') return v()
  return v
}

// Normalize any month input (moment / Date / 'YYYY-MM' / {year, month}) to a moment at startOf month.
function normalizeMonth (m) {
  if (m == null) return moment().startOf('month')
  if (moment.isMoment(m)) return m.clone().startOf('month')
  if (m instanceof Date) return moment(m).startOf('month')
  if (typeof m === 'object' && 'year' in m && 'month' in m) {
    return moment().year(m.year).month(m.month).startOf('month')
  }
  return moment(m).startOf('month')
}

// Booking count for a day — decoupled from the Order shape: array → length, number → itself.
function countOf (value) {
  if (Array.isArray(value)) return value.length
  if (typeof value === 'number') return value
  return 0
}

export function useMonthCalendar (options = {}) {
  // Internal viewed month. Kept in sync with the (parent-owned) source when it is reactive, so
  // the "parent owns state; drives inner via public API" convention holds — next()/prev() mutate
  // this ref AND the caller re-drives it via the prop; the watch makes that idempotent.
  const monthRef = ref(normalizeMonth(read(options.month)))
  if (isRef(options.month) || typeof options.month === 'function') {
    watch(() => read(options.month), (m) => { monthRef.value = normalizeMonth(m) })
  }

  // Internal selection — same sync strategy as the month.
  const selectedRef = ref(read(options.selectedDate) || null)
  if (isRef(options.selectedDate) || typeof options.selectedDate === 'function') {
    watch(() => read(options.selectedDate), (v) => { selectedRef.value = v || null })
  }

  const weekStartsOn = computed(() => {
    const v = read(options.weekStartsOn)
    return (typeof v === 'number') ? v : 0
  })

  const weekdays = computed(() => {
    const base = read(options.weekdays) || DEFAULT_WEEKDAYS
    const s = weekStartsOn.value % 7
    return base.slice(s).concat(base.slice(0, s))
  })

  const titleParts = computed(() => {
    const m = monthRef.value
    return {
      monthIndex: m.month(),
      gregorianYear: m.year(),
      buddhistYear: m.year() + 543
    }
  })

  // Day-grid cells — identical shape/order to the original calendarCells, plus isToday/isSelected.
  const cells = computed(() => {
    const start = monthRef.value.clone().startOf('month')
    const daysInMonth = start.daysInMonth()
    // Leading blanks before day 1 (Sun-first when weekStartsOn = 0 → matches the original start.day()).
    const leading = (start.day() - weekStartsOn.value + 7) % 7
    const map = read(options.bookingsByDay) || {}
    const today = read(options.today) || moment().format('YYYY-MM-DD')
    const selected = selectedRef.value || null
    const out = []
    for (let i = 0; i < leading; i++) {
      out.push({ blank: true, key: 'blank-' + i })
    }
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = start.clone().date(d).format('YYYY-MM-DD')
      out.push({
        blank: false,
        key: dateStr,
        day: d,
        dateStr,
        count: countOf(map[dateStr]),
        isToday: dateStr === today,
        isSelected: dateStr === selected
      })
    }
    return out
  })

  // Pure state mutators — return the new 'YYYY-MM' so the caller can re-fetch that month.
  function goToMonth (m) {
    monthRef.value = normalizeMonth(m)
    return monthRef.value.format('YYYY-MM')
  }
  function next () {
    monthRef.value = monthRef.value.clone().add(1, 'month')
    return monthRef.value.format('YYYY-MM')
  }
  function prev () {
    monthRef.value = monthRef.value.clone().subtract(1, 'month')
    return monthRef.value.format('YYYY-MM')
  }
  function select (dateStr) {
    selectedRef.value = dateStr || null
    return selectedRef.value
  }

  return { cells, titleParts, weekdays, next, prev, goToMonth, select }
}

export default useMonthCalendar
